import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { getUrlParamsFetch, urlBackend } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { TiposRegimen, TiposRegimenPagingRes } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchTiposRegimen = (
  params?: GetTiposRegimenParams,
  enabled = true
) => {
  return useQuery({
    queryKey: ['tiposRegimen', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getTiposRegimen(params);
    },
    enabled: enabled,
  });
};

export const useGetTipoRegimen = (id: string) => {
  return useQuery({
    queryKey: ['tiposRegimen', id],
    queryFn: async () => {
      return getTipoRegimen(id);
    },
  });
};

export const useCreateTipoRegimen = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTipoRegimen,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiposRegimen'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

export const useUpdateTipoRegimen = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTipoRegimen,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiposRegimen'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

//* axios
export type GetTiposRegimenParams = Partial<TiposRegimen> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateTipoRegimenParams = Omit<TiposRegimen, 'id_tipo_regimen'>;
export type UpdateTipoRegimenParams = {
  id: string;
  data: CreateTipoRegimenParams;
};

export const getTiposRegimen = async (params?: GetTiposRegimenParams) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<TiposRegimenPagingRes>(
    `${urlBackend}api/v1/tipo-regimen/?${queryParams}`,
    true
  );
};

export const getTipoRegimen = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<TiposRegimen>(
    `${urlBackend}api/v1/tipo-regimen/${id}/`,
    true
  );

  res.status === 'error' && setAlert(true, res.message, res.status);
  return res;
};

export const createTipoRegimen = async (data: CreateTipoRegimenParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<TiposRegimen>(
    `${urlBackend}api/v1/tipo-regimen/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateTipoRegimen = async (params: UpdateTipoRegimenParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<TiposRegimen>(
    `${urlBackend}api/v1/tipo-regimen/${params.id}/`,
    params.data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
